/**
 * Filtros da caixa de entrada de publicações e sua representação na URL
 * (para o link/aba do navegador manterem advogado, estado e filtros).
 */
import type { InboxState } from '@/services/publication-inbox-service'

export const INBOX_STATES: Array<{ value: InboxState; label: string }> = [
  { value: 'NOVA', label: 'Nova' },
  { value: 'EM_TRIAGEM', label: 'Em triagem' },
  { value: 'PRAZO_LANCADO', label: 'Prazo lançado' },
  { value: 'SEM_PROVIDENCIA', label: 'Sem providência' },
  { value: 'CONFERIDA', label: 'Conferidas' },
]

/** Estados em que a publicação pode ser marcada como conferida / ter a conferência desfeita. */
export function conferenceActionFor(estado: InboxState): 'conferir' | 'desconferir' | null {
  if (estado === 'NOVA' || estado === 'EM_TRIAGEM') return 'conferir'
  if (estado === 'CONFERIDA') return 'desconferir'
  return null
}

export interface InboxFilters {
  /** 'todos' | 'eu' | id do usuário */
  advogado: string
  estado: InboxState
  processoVinculado?: 'sim' | 'nao'
  /** id do usuário ou 'none' */
  responsavel?: string
  tribunal?: string
  dataInicio?: string
  dataFim?: string
  q?: string
  page: number
}

export const DEFAULT_INBOX_FILTERS: InboxFilters = { advogado: 'todos', estado: 'NOVA', page: 1 }

const OPTIONAL_KEYS = ['processoVinculado', 'responsavel', 'tribunal', 'dataInicio', 'dataFim', 'q'] as const
const DATE = /^\d{4}-\d{2}-\d{2}$/

function isState(value: string | null): value is InboxState {
  return INBOX_STATES.some((s) => s.value === value)
}

export function filtersFromSearch(params: URLSearchParams): InboxFilters {
  const filters: InboxFilters = {
    advogado: params.get('advogado') || DEFAULT_INBOX_FILTERS.advogado,
    estado: isState(params.get('estado')) ? (params.get('estado') as InboxState) : DEFAULT_INBOX_FILTERS.estado,
    page: Math.max(Number.parseInt(params.get('pagina') ?? '1', 10) || 1, 1),
  }
  const vinculado = params.get('processoVinculado')
  if (vinculado === 'sim' || vinculado === 'nao') filters.processoVinculado = vinculado
  for (const key of ['responsavel', 'tribunal', 'q'] as const) {
    const value = params.get(key)?.trim()
    if (value) filters[key] = value
  }
  for (const key of ['dataInicio', 'dataFim'] as const) {
    const value = params.get(key)
    if (value && DATE.test(value)) filters[key] = value
  }
  return filters
}

/** Só grava na URL o que difere do padrão. */
export function filtersToSearch(filters: InboxFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.advogado !== DEFAULT_INBOX_FILTERS.advogado) params.set('advogado', filters.advogado)
  if (filters.estado !== DEFAULT_INBOX_FILTERS.estado) params.set('estado', filters.estado)
  for (const key of OPTIONAL_KEYS) {
    const value = filters[key]
    if (value) params.set(key, value)
  }
  if (filters.page > 1) params.set('pagina', String(filters.page))
  return params
}

/** Parâmetros da API (GET /api/publications/inbox). */
export function toApiParams(filters: InboxFilters): Record<string, string | number> {
  const params: Record<string, string | number> = { advogado: filters.advogado, estado: filters.estado, page: filters.page, limit: 30 }
  for (const key of OPTIONAL_KEYS) {
    const value = filters[key]
    if (value) params[key] = value
  }
  return params
}

/** Muda filtros e volta para a 1ª página (exceto quando só a página muda). */
export function updateFilters(current: InboxFilters, patch: Partial<InboxFilters>): InboxFilters {
  const next = { ...current, ...patch }
  for (const key of OPTIONAL_KEYS) {
    if (key in patch && !patch[key]) delete next[key]
  }
  if (!('page' in patch)) next.page = 1
  return next
}

export function activeChips(filters: InboxFilters, names: { responsavel?: (id: string) => string } = {}): Array<{ key: (typeof OPTIONAL_KEYS)[number]; label: string }> {
  const chips: Array<{ key: (typeof OPTIONAL_KEYS)[number]; label: string }> = []
  if (filters.processoVinculado) chips.push({ key: 'processoVinculado', label: filters.processoVinculado === 'sim' ? 'Com processo vinculado' : 'Sem processo vinculado' })
  if (filters.responsavel) {
    chips.push({ key: 'responsavel', label: filters.responsavel === 'none' ? 'Sem responsável' : `Responsável: ${names.responsavel?.(filters.responsavel) ?? '…'}` })
  }
  if (filters.tribunal) chips.push({ key: 'tribunal', label: `Tribunal: ${filters.tribunal}` })
  if (filters.dataInicio) chips.push({ key: 'dataInicio', label: `De ${formatDateBr(filters.dataInicio)}` })
  if (filters.dataFim) chips.push({ key: 'dataFim', label: `Até ${formatDateBr(filters.dataFim)}` })
  if (filters.q) chips.push({ key: 'q', label: `Busca: “${filters.q}”` })
  return chips
}

export function formatDateBr(iso: string | null | undefined): string {
  if (!iso) return ''
  const [y, m, d] = iso.slice(0, 10).split('-')
  return d && m && y ? `${d}/${m}/${y}` : iso
}

const PRIORITY_LABELS: Record<string, string> = { LOW: 'Baixa', MEDIUM: 'Média', HIGH: 'Alta', URGENT: 'Urgente' }

/** Rótulo em português da prioridade da publicação (aceita o valor em qualquer caixa). */
export function priorityLabel(priority: string | null | undefined): string {
  if (!priority) return ''
  return PRIORITY_LABELS[priority.toUpperCase()] ?? priority
}

/**
 * Texto da publicação como chega do DJEN, com as quebras em `<br>` (cruas ou escapadas)
 * trocadas por quebras de linha — para exibir como texto, sem interpretar HTML.
 * Um `<br` cortado no fim (resumo truncado) é descartado.
 */
export function publicationPlainText(text: string | null | undefined): string {
  if (!text) return ''
  return text
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/&lt;br\s*\/?\s*&gt;/gi, '\n')
    .replace(/<(?:b(?:r\s*\/?)?)?$/i, '')
}
